import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ArrowUpRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { NAV, CLUB } from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-ink/10 shadow-[0_8px_32px_rgba(62,106,158,0.08)]"
          : "bg-gradient-to-b from-cab-deep/50 via-cab-deep/15 to-transparent backdrop-blur-[2px] border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between gap-6 px-5 md:px-8">
        {/* Brand */}
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="group flex items-center gap-3"
        >
          <img
            src="/brand/logo-cab.png"
            alt="Club Atletico Bologna 1948"
            className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <span className="hidden sm:flex flex-col leading-none">
            <span className={`font-display text-[15px] font-extrabold uppercase tracking-tight transition-colors ${scrolled ? "text-ink" : "text-cream"}`}>
              Club Atletico Bologna
            </span>
            <span className={`font-display text-[11px] font-bold uppercase tracking-[0.35em] transition-colors ${scrolled ? "text-cab" : "text-cab-light"}`}>
              CAB · {CLUB.founded}
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? scrolled ? "text-cab" : "text-white"
                    : scrolled ? "text-ink/70 hover:text-ink" : "text-cream/80 hover:text-white"
                } after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 ${
                  scrolled ? "after:bg-cab" : "after:bg-cream"
                } after:transition-transform after:duration-300 hover:after:scale-x-100 ${
                  isActive ? "after:scale-x-100" : ""
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/dove-siamo"
            data-testid="header-cta-button"
            className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-cab px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(62,106,158,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-cab-dark"
          >
            Prova gratuita
            <ArrowUpRight className="h-4 w-4" />
          </Link>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Apri menu"
                className={`inline-flex h-11 w-11 items-center justify-center rounded-md border transition-colors lg:hidden ${
                  scrolled ? "border-ink/15 bg-white/70 text-ink" : "border-cream/30 bg-cream/10 text-cream"
                }`}
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86%] max-w-sm border-l border-ink/10 bg-cream p-0"
              data-testid="mobile-menu-content"
            >
              <div className="flex items-center gap-3 border-b border-ink/10 px-6 py-6">
                <img src="/brand/logo-cab.png" alt="CAB" fetchpriority="high" decoding="async" className="h-10 w-10 object-contain" />
                <span className="font-display text-sm font-extrabold uppercase tracking-tight text-ink">
                  Club Atletico Bologna
                </span>
              </div>
              <nav className="flex flex-col px-3 py-4">
                {NAV.map((item, i) => (
                  <SheetClose asChild key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={`mobile-nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between gap-4 rounded-lg px-4 py-3.5 font-display text-lg font-bold transition-colors ${
                          isActive ? "bg-cab/10 text-cab" : "text-ink hover:bg-ink/5"
                        }`
                      }
                    >
                      <span>{item.label}</span>
                      <span className="font-sans text-xs font-bold text-cab/50">
                        0{i + 1}
                      </span>
                    </NavLink>
                  </SheetClose>
                ))}
              </nav>
              <div className="px-6 pt-2">
                <SheetClose asChild>
                  <Link
                    to="/dove-siamo"
                    data-testid="mobile-cta-button"
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-cab px-5 py-3.5 font-bold text-white"
                  >
                    Prenota una prova gratuita
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
